import React, { useEffect, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const items = [
    { id: "story", label: t.nav.story },
    { id: "itinerary", label: t.nav.itinerary },
    { id: "venue", label: t.nav.venue },
    { id: "details", label: t.nav.details },
    { id: "travel", label: t.nav.travel },
    { id: "gallery", label: t.nav.gallery },
    { id: "rsvp", label: t.nav.rsvp },
  ];

  const handleClick = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      window.location.href = `/#${id}`;
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[var(--ivory)]/95 backdrop-blur-md shadow-[0_1px_0_var(--border-soft)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between py-5">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center"
        >
          <img
            src={scrolled
              ? "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/itxixsdb_Monogram_526E3B.png"
              : "https://customer-assets.emergentagent.com/job_boda-invitation-hub/artifacts/2mj9rj9e_Monogram_F9F6EF.png"}
            alt="A&E monogram"
            className="h-10 md:h-12 w-auto object-contain transition-opacity duration-300"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {items.map((it) => (
            <button
              key={it.id}
              data-testid={`nav-${it.id}`}
              onClick={() => handleClick(it.id)}
              className={`text-[11px] tracking-[0.22em] uppercase font-bold transition-colors duration-300 ${
                scrolled ? "text-[var(--text-primary)] hover:text-[var(--sage-deep)]" : "text-white hover:text-[var(--sage-soft)]"
              }`}
            >
              {it.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            data-testid="language-toggle"
            className={`flex items-center text-[11px] tracking-[0.22em] border ${
              scrolled ? "border-[var(--border-soft)]" : "border-white/40"
            } rounded-full overflow-hidden`}
          >
            <button
              data-testid="lang-es"
              onClick={() => setLang("es")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "es"
                  ? "bg-[var(--sage-deep)] text-white"
                  : scrolled
                    ? "text-[var(--text-primary)]"
                    : "text-white"
              }`}
            >
              ES
            </button>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en"
                  ? "bg-[var(--sage-deep)] text-white"
                  : scrolled
                    ? "text-[var(--text-primary)]"
                    : "text-white"
              }`}
            >
              EN
            </button>
          </div>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className={`md:hidden ml-2 ${scrolled ? "text-[var(--text-primary)]" : "text-white"}`}
            aria-label="Menu"
          >
            <span className="block w-6 h-px bg-current mb-1.5" />
            <span className="block w-6 h-px bg-current mb-1.5" />
            <span className="block w-6 h-px bg-current" />
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden bg-[var(--ivory)] border-t border-[var(--border-soft)]">
          <div className="flex flex-col px-6 py-4 gap-4">
            {items.map((it) => (
              <button
                key={it.id}
                data-testid={`mobile-nav-${it.id}`}
                onClick={() => handleClick(it.id)}
                className="text-left text-[12px] tracking-[0.22em] uppercase font-bold text-[var(--text-primary)]"
              >
                {it.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
